/**
 * CrearPlantilla — el formulario de creación de plantillas de WhatsApp (Paso 4b).
 *
 * NO SUGIERE NOMBRES. Ni autocompletado, ni plantillas de ejemplo por vertical, ni un
 * placeholder del tipo «recordatorio_cita_dental». Una plantilla vive en el WABA del
 * cliente y un nombre derivado de su vertical roza la sección 1.h de las Condiciones
 * de Herramientas Empresariales de Meta. El riesgo es del cliente; PUX no lo induce.
 * Esto es requisito, no preferencia: si alguien añade sugerencias aquí, lo rompe.
 *
 * EL EJEMPLO ES LA VISTA PREVIA. No son dos campos distintos: los valores de ejemplo
 * que Meta exige para revisar la plantilla son los mismos con los que se compone lo
 * que el usuario ve antes de enviar.
 *
 * LA VALIDACIÓN QUE MANDA ES LA DEL BACKEND. Aquí sólo se guía —cuántos campos de
 * ejemplo pintar, si el botón está habilitado— y se muestra lo que responde el
 * endpoint, que valida de verdad antes de llamar a Meta.
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { waCloudAPI } from '../../services/api';
import { variablesOrdenadas, numeracionCorrecta, componerVistaPrevia } from '../../utils/variablesPlantilla';

/**
 * ═══════════════════════════════════════════════════════════════════════════════
 *  NO SE LE PREGUNTA LA CATEGORÍA AL CLIENTE · SE LE PREGUNTA SU INTENCIÓN
 * ═══════════════════════════════════════════════════════════════════════════════
 * Aquí había un `<select>` con `['UTILITY', 'MARKETING', 'AUTHENTICATION']` y `UTILITY`
 * preseleccionado. **Se retiró el 28-sep-2026, y el motivo no es de diseño: es que la
 * pregunta era imposible de responder.**
 *
 * LA CATEGORÍA NO LA DETERMINA EL TEXTO. Medido con el MISMO texto en tres WABAs:
 * `retomar_conversacion` salió UTILITY en la de Casanuestra y MARKETING en las de PUX y
 * Brokers. Depende de algo de la cuenta que no vemos.
 *
 * Y el selector no decía nada — ahí estaba lo caro: **pedirle elegir algo que no controla
 * ES la promesa.** Un desplegable con `UTILITY` por defecto comunica «esto lo decides tú»,
 * y a dos líneas había un texto que decía lo contrario («la categoría la fija Meta»). El
 * cliente resolvía la contradicción de la forma natural —«la elijo y Meta la confirma»—
 * que es justo lo que la medición desmiente. Peor aún, la misma frase mencionaba el
 * PRECIO, así que elegir `UTILITY` parecía una forma de pagar menos.
 *
 * ⚠️ LA DISTINCIÓN QUE JUSTIFICA LA CASILLA DE ABAJO, y es la que hay que retener:
 *
 *     «¿qué categoría quieres?»          → una decisión de META. Nadie aquí la controla
 *     «¿esto es para promociones?»       → una decisión SUYA. Él la sabe y la decide
 *
 * Se pregunta lo segundo, y lo que esa respuesta gobierna es **el pie de baja**, no la
 * categoría. Son dos cosas distintas y no deben volver a mezclarse.
 *
 * `AUTHENTICATION` desaparece y nunca tuvo defensa: `politicaConsentimiento` la bloquea en
 * campañas —«los códigos de verificación se envían uno a uno»— así que ofrecerla
 * garantizaba una plantilla inservible en el único sitio desde donde se envía.
 *
 * La categoría que se pide a Meta la fija el SERVIDOR (`createWhatsAppTemplate.js`), no
 * este formulario: si viajara en el cuerpo, un cliente podría seguir mandando MARKETING
 * por la API aunque la UI no lo ofrezca — el mismo agujero que el control del
 * `templateCategory` falseado cerró en el endpoint de envío.
 * ═══════════════════════════════════════════════════════════════════════════════
 */
const IDIOMAS = ['es', 'es_MX', 'en_US', 'pt_BR'];

const caja = {
  padding: '0.5rem', borderRadius: '6px', border: '1px solid #ccc', width: '100%', boxSizing: 'border-box',
};
const etiqueta = { display: 'block', fontWeight: 600, marginBottom: '0.35rem', fontSize: '0.85rem' };

export default function CrearPlantilla({ numero, organizationId, onCreada }) {
  const { t } = useTranslation('campaigns');

  const [abierto, setAbierto] = useState(false);
  const [nombre, setNombre] = useState('');
  const [idioma, setIdioma] = useState('es_MX');
  // Su INTENCIÓN, no la categoría. Desmarcada por defecto: el caso corriente es una
  // plantilla de servicio, y marcar por omisión metería un «responde BAJA» en
  // recordatorios de cita — invitando a darse de baja de algo que la persona sí quiere.
  const [esPromocional, setEsPromocional] = useState(false);
  const [cuerpo, setCuerpo] = useState('');
  const [ejemplos, setEjemplos] = useState({});      // { [indice]: valor }
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);  // {tipo, texto}

  const indices = useMemo(() => variablesOrdenadas(cuerpo), [cuerpo]);
  const numeracionOk = useMemo(() => numeracionCorrecta(cuerpo), [cuerpo]);
  const vistaPrevia = useMemo(() => componerVistaPrevia(cuerpo, ejemplos), [cuerpo, ejemplos]);

  const faltanEjemplos = indices.some((n) => !(ejemplos[n] || '').trim());
  const puedeEnviar = !enviando && nombre.trim() && cuerpo.trim() && numeracionOk && !faltanEjemplos;

  // Vacía los CAMPOS, no el resultado. Se llama justo después de fijar el mensaje de
  // éxito: si borrara también `resultado`, el usuario no llegaría a ver que su
  // plantilla se envió. Lo detectó el arnés, no la revisión.
  const limpiarCampos = () => {
    setNombre(''); setCuerpo(''); setEjemplos({});
  };

  const enviar = async () => {
    setEnviando(true);
    setResultado(null);
    try {
      const r = await waCloudAPI.createTemplate({
        phoneNumberId: numero?.phone_number_id,
        wabaId: numero?.waba_id,
        organizationId,
        nombre,
        idioma,
        esPromocional,
        // En el orden de los índices, que es el orden en el que Meta sustituye.
        ejemplos: indices.map((n) => ejemplos[n]),
        cuerpo,
      });

      if (r?.foundExisting) {
        // El backend adopta la existente cuando Meta dice duplicado. Desde aquí eso NO
        // puede leerse como «creada»: no se creó nada, ya estaba.
        setResultado({ tipo: 'aviso', texto: t('crearPlantilla.yaExiste') });
      } else if (r?.status === 'REJECTED') {
        // Rechazo inmediato de Meta. El motivo se muestra tal cual lo devuelve: es un
        // código suyo, y traducirlo a una frase nuestra ocultaría cuál fue.
        const motivo = r.rejectedReason || r.reason;
        setResultado({
          tipo: 'error',
          texto: motivo
            ? `${t('crearPlantilla.rechazada')} ${t('crearPlantilla.motivoMeta')}: ${motivo}`
            : t('crearPlantilla.rechazada'),
        });
      } else {
        setResultado({ tipo: 'ok', texto: t('crearPlantilla.enviada') });
        limpiarCampos();
      }

      // U3 · la plantilla aparece en el listado con su estado sin recargar a mano.
      // Se refresca en los tres casos: también la duplicada y la rechazada existen en
      // Meta y el usuario tiene que verlas.
      if (onCreada) await onCreada();
    } catch (err) {
      // El backend responde 400 con `message` en claro cuando el payload no pasa su
      // validación. Si no lo hay, se muestra lo que venga antes que un texto genérico.
      setResultado({ tipo: 'error', texto: err?.message || t('crearPlantilla.errorGenerico') });
    } finally {
      setEnviando(false);
    }
  };

  if (!abierto) {
    return (
      <button
        type="button"
        onClick={() => setAbierto(true)}
        style={{ ...caja, width: 'auto', cursor: 'pointer', background: '#fff', fontWeight: 600 }}
      >
        {t('crearPlantilla.abrir')}
      </button>
    );
  }

  const colorResultado = { ok: '#1e8e3e', aviso: '#8a6d00', error: '#c5221f' }[resultado?.tipo] || '#5f6368';

  return (
    <div style={{ border: '1px solid #dadce0', borderRadius: '8px', padding: '1rem', marginBottom: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <strong>{t('crearPlantilla.titulo')}</strong>
        <button type="button" onClick={() => { setAbierto(false); limpiarCampos(); setResultado(null); }} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#5f6368' }}>
          {t('crearPlantilla.cerrar')}
        </button>
      </div>

      {/* U2 · la aprobación no es inmediata y la campaña no se puede lanzar hasta que llegue. */}
      <p style={{ fontSize: '0.8rem', color: '#5f6368', backgroundColor: '#f1f3f4', padding: '0.5rem', borderRadius: '6px', marginTop: 0 }}>
        {t('crearPlantilla.avisoAprobacion')}
      </p>

      <div style={{ marginBottom: '0.75rem' }}>
        <label style={etiqueta} htmlFor="plantilla-nombre">{t('crearPlantilla.nombre')}</label>
        {/* Sin `placeholder` de ejemplo y sin autocompletado: ver la cabecera (U5). */}
        <input
          id="plantilla-nombre"
          type="text"
          value={nombre}
          autoComplete="off"
          onChange={(e) => setNombre(e.target.value)}
          style={caja}
        />
        <small style={{ color: '#5f6368' }}>{t('crearPlantilla.nombreAyuda')}</small>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: '140px' }}>
          <label style={etiqueta} htmlFor="plantilla-idioma">{t('crearPlantilla.idioma')}</label>
          <select id="plantilla-idioma" value={idioma} onChange={(e) => setIdioma(e.target.value)} style={caja}>
            {IDIOMAS.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
        </div>
      </div>

      {/* LA INTENCIÓN, NO LA CATEGORÍA. Ver la cabecera: lo que esta casilla gobierna es
          el pie de baja que exige el art. 28 B para comunicaciones promocionales. */}
      <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', marginBottom: '0.5rem', cursor: 'pointer' }}>
        <input
          type="checkbox"
          id="plantilla-promocional"
          checked={esPromocional}
          onChange={(e) => setEsPromocional(e.target.checked)}
          style={{ marginTop: '0.2rem' }}
        />
        <span style={{ fontSize: '0.85rem' }}>{t('crearPlantilla.esPromocional')}</span>
      </label>

      {/* Informativo y sin cifras inventadas: el precio lo pone Meta en la cuenta del
          cliente y no lo conocemos desde aquí. Y ya NO insinúa que se pueda elegir. */}
      <p style={{ fontSize: '0.78rem', color: '#5f6368', marginTop: 0 }}>
        {t('crearPlantilla.avisoCategoria')}
      </p>

      <div style={{ marginBottom: '0.75rem' }}>
        <label style={etiqueta} htmlFor="plantilla-cuerpo">{t('crearPlantilla.cuerpo')}</label>
        <textarea
          id="plantilla-cuerpo"
          value={cuerpo}
          rows={4}
          onChange={(e) => setCuerpo(e.target.value)}
          style={{ ...caja, fontFamily: 'inherit', resize: 'vertical' }}
        />
        <small style={{ color: '#5f6368' }}>{t('crearPlantilla.cuerpoAyuda')}</small>
        {!numeracionOk && (
          <div style={{ fontSize: '0.78rem', color: '#c5221f', marginTop: '0.25rem' }}>
            {t('crearPlantilla.numeracionMal')}
          </div>
        )}
      </div>

      {indices.length > 0 && (
        <div style={{ marginBottom: '0.75rem' }}>
          <label style={etiqueta}>{t('crearPlantilla.ejemplos')}</label>
          <small style={{ color: '#5f6368', display: 'block', marginBottom: '0.4rem' }}>
            {t('crearPlantilla.ejemplosAyuda')}
          </small>
          {indices.map((n) => (
            <div key={n} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.4rem' }}>
              <span style={{ minWidth: '52px', fontSize: '0.85rem' }}>{`{{${n}}}`}</span>
              <input
                type="text"
                aria-label={`{{${n}}}`}
                value={ejemplos[n] || ''}
                autoComplete="off"
                onChange={(e) => setEjemplos((p) => ({ ...p, [n]: e.target.value }))}
                style={caja}
              />
            </div>
          ))}
        </div>
      )}

      {cuerpo.trim() && (
        <div style={{ marginBottom: '0.75rem' }}>
          <label style={etiqueta}>{t('crearPlantilla.vistaPrevia')}</label>
          <div style={{ whiteSpace: 'pre-wrap', backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: '6px', padding: '0.6rem', fontSize: '0.88rem' }}>
            {vistaPrevia}
          </div>
        </div>
      )}

      {resultado && (
        <div style={{ color: colorResultado, fontSize: '0.85rem', marginBottom: '0.6rem' }}>
          {resultado.texto}
        </div>
      )}

      <button
        type="button"
        onClick={enviar}
        disabled={!puedeEnviar}
        style={{
          ...caja, width: 'auto', fontWeight: 600, cursor: puedeEnviar ? 'pointer' : 'not-allowed',
          backgroundColor: puedeEnviar ? '#1a73e8' : '#e0e0e0',
          color: puedeEnviar ? '#fff' : '#9e9e9e', border: 'none',
        }}
      >
        {enviando ? t('crearPlantilla.enviando') : t('crearPlantilla.enviar')}
      </button>
    </div>
  );
}
